import { createContext, useContext, useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  USER_MANAGEMENT_KEYS,
  USER_ROLE_KEYS,
} from '../../../../utilities/constants';
import { API } from '../../../../services';
import { ErrorMessageContext } from '../../../../context/ErrorMessageContext';
import { UserRolesAndPermissionsContext } from '../../../../context/UserRolesAndPermissionsContext';
import { useAuth } from '../../../../context/AuthContext';
import { getErrorMessage } from '../../../../utilities/commonFunctions';

const CompanyManagementContext = createContext(null);

export const CompanyManagementProvider = ({ children }) => {
  const { userRole } = useContext(UserRolesAndPermissionsContext);
  const { isLoggedIn } = useAuth();
  const [pageActiveTabKey, setPageActiveTabKey] =
    useState('companies-overview');
  const [activeTabKey, setActiveKey] = useState(USER_MANAGEMENT_KEYS.COMPANIES);
  const { addErrorMessage } = useContext(ErrorMessageContext);
  const [organizationData, setOrganizationData] = useState([]);
  const [orgList, setOrgList] = useState([]);
  const [branchData, setBranchData] = useState([]);
  const [organizationDetails, setOrganizationDetails] = useState({});
  const [summaryUpdatedTime, setSummaryUpdatedTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loginFeaturesList, setLoginFeaturesList] = useState([]);
  const [loginMethodOptions, setLoginMethodOptions] = useState([]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const { data } = await API.getCompaniesList({ skipRouteAbort: true });
      if (data) {
        return data;
      }
    } catch (error) {
      addErrorMessage(
        getErrorMessage(
          error,
          'Unable to fetch companies list! Please try again after sometime.'
        )
      );
      return [];
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizationData = async () => {
    const data = await fetchData();
    const formattedData = data?.map((company, index) => ({
      ...company,
      location: (company?.location || []).map(loc => ({
        ...loc,
        address: [loc?.address1, loc?.address2].filter(Boolean).join(', '),
      })),
      key: company?.org_id,
      indexKey: index,
      registered_address: [company?.address1, company?.address2]
        .filter(Boolean)
        .join(', '),
      company: company?.org_name,
      registration_date: company?.registered_timestamp,
    }));
    setOrganizationData(formattedData);
    const formattedOrgListData = data?.map(company => ({
      value: company?.org_id,
      label: company?.org_name,
    }));
    setOrgList(formattedOrgListData);
  };

  const onTabChange = key => {
    setActiveKey(key);
  };

  useEffect(() => {
    if (!isLoggedIn) return;
    (userRole === USER_ROLE_KEYS.SUPER_ADMIN ||
      userRole === USER_ROLE_KEYS.PLATFORM_OPERATION_MANAGER) &&
      fetchOrganizationData();
  }, [isLoggedIn, userRole]);

  const fetchBranchData = async () => {
    const data = await fetchData();
    const formattedData = data?.[0]?.location?.map((location, index) => ({
      key: index,
      loc_id: location?.loc_id,
      state: location?.state,
      branch_name: location?.branch_name,
      city: location?.city,
      locality: location?.locality,
      address: location?.address1 + ', ' + location?.address2,
      registration_date: location?.registered_timestamp,
      address1: location?.address1,
      address2: location?.address2,
      pin_code: location?.pin_code,
      org_id: data?.[0]?.org_id,
    }));

    setBranchData(formattedData);
    setOrganizationDetails({
      org_name: data?.[0]?.org_name,
      primary_email: data?.[0]?.primary_email,
      address: data?.[0]?.address1 + data?.[0]?.address2,
    });
  };

  const fetchLoginFeaturesList = async () => {
    try {
      const { data } = await API.getAllFeatures();
      if (data) {
        const formattedData = data
          ?.filter(item => item.category === 'login_feature')
          ?.map(item => ({
            feature_id: item?.feature_id,
            feature_name: item?.feature_name,
            description: item?.description,
          }));
        setLoginFeaturesList(formattedData);
        setLoginMethodOptions(
          formattedData?.map(feature => ({
            label:
              feature?.feature_name === 'u_p'
                ? 'Email & password'
                : feature?.feature_name,
            value: feature?.feature_id,
          })) || []
        );
      }
    } catch (error) {
      addErrorMessage(
        getErrorMessage(
          error,
          'Unable to fetch login features list! Please try again after sometime.'
        )
      );
      return [];
    } finally {
      setLoading(false);
    }
  };

  const contextValue = useMemo(
    () => ({
      pageActiveTabKey,
      setPageActiveTabKey,
      activeTabKey,
      onTabChange,
      loading,
      organizationData,
      setOrganizationData,
      fetchOrganizationData,
      branchData,
      setBranchData,
      organizationDetails,
      fetchBranchData,
      summaryUpdatedTime,
      setSummaryUpdatedTime,
      orgList,
      loginFeaturesList,
      loginMethodOptions,
      fetchLoginFeaturesList,
    }),
    [
      pageActiveTabKey,
      activeTabKey,
      organizationData,
      loading,
      branchData,
      organizationDetails,
      fetchOrganizationData,
      summaryUpdatedTime,
      orgList,
      loginFeaturesList,
      loginMethodOptions,
    ]
  );

  return (
    <CompanyManagementContext.Provider value={contextValue}>
      {children}
    </CompanyManagementContext.Provider>
  );
};

CompanyManagementProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useCompanyManagement = () => useContext(CompanyManagementContext);
